// Etiquetas de apertura, elaboración y congelación: funciones puras que usa la
// página Etiquetas (buscar sin tildes, fechas dd/mm/aa en hora de Madrid y la
// caducidad prevista calculada igual que el backend, etiquetas_routes.py).
import { hoyMadrid } from './fechas';

export const SECO = 'seco';
export const REFRIGERADO = 'refrigerado';
export const CONGELADO = 'congelado';
export const CONSERVACIONES = [SECO, REFRIGERADO, CONGELADO];

export const NOMBRE_CONSERVACION = {
  [SECO]: 'Seco',
  [REFRIGERADO]: 'Refrigerado',
  [CONGELADO]: 'Congelado',
};

// Mismos textos que RANGO_POR_CONSERVACION de etiquetas_reglas.py
export const RANGO_POR_CONSERVACION = {
  [SECO]: 'Lugar fresco y seco',
  [REFRIGERADO]: '0 a 4 °C',
  [CONGELADO]: '-18 °C o menos',
};

export const VIDA_CONGELADO_DIAS = 90;
export const VIDA_DESCONGELADO_HORAS = 24;

// Cómo se mide la vida de un ingrediente abierto (etiquetas_reglas.VIDA_MODOS)
export const VIDA_MODO_DIAS = 'dias';
export const VIDA_MODO_ENVASE = 'envase';
export const TEXTO_HASTA_ENVASE = 'Hasta la fecha del envase';
export const TEXTO_FALTA_ENVASE = 'Indica la caducidad del envase';
// Tope del APPCC de Duetto para refrigerados (120 h)
export const TOPE_REFRIGERADO_DIAS_APPCC = 5;
export const AVISO_SUPERA_APPCC = 'Supera las 120 h que fija el APPCC para refrigerados.';

export const esHastaEnvase = (producto) => !!producto && producto.tipo !== 'plato'
  && producto.vida_modo === VIDA_MODO_ENVASE;

const pad2 = (n) => String(n).padStart(2, '0');

// ==================== BÚSQUEDA ====================

// Minúsculas, sin tildes y con los espacios juntos
export function normalizarTexto(texto) {
  return String(texto || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

// Todas las palabras de la búsqueda aparecen en el texto (en cualquier orden)
export function coincideBusqueda(texto, busqueda) {
  const q = normalizarTexto(busqueda);
  if (!q) return true;
  const t = normalizarTexto(texto);
  return q.split(' ').every((palabra) => t.includes(palabra));
}

// ==================== FECHAS ====================

// Ahora en hora de Madrid: 'YYYY-MM-DDTHH:MM'
export function ahoraMadrid(ahora = new Date()) {
  try {
    const partes = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Madrid',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(ahora);
    const v = (tipo) => (partes.find((p) => p.type === tipo) || {}).value;
    if (v('year') && v('month') && v('day') && v('hour') && v('minute')) {
      return `${v('year')}-${v('month')}-${v('day')}T${v('hour')}:${v('minute')}`;
    }
  } catch (e) {
    // Sin soporte de zonas horarias: la hora local del navegador
  }
  return `${hoyMadrid(ahora)}T${pad2(ahora.getHours())}:${pad2(ahora.getMinutes())}`;
}

// 'YYYY-MM-DD[THH:MM]' -> { anio, mes, dia, hora, minuto } (o null)
export function partesFecha(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  if (!m) return null;
  return {
    anio: Number(m[1]), mes: Number(m[2]), dia: Number(m[3]),
    hora: m[4] !== undefined ? Number(m[4]) : null,
    minuto: m[5] !== undefined ? Number(m[5]) : null,
  };
}

// Suma días a una fecha de calendario 'YYYY-MM-DD' (en UTC: sin cambios de hora)
export function sumarDias(fecha, dias) {
  const p = partesFecha(fecha);
  if (!p) return '';
  const d = new Date(Date.UTC(p.anio, p.mes - 1, p.dia + Number(dias || 0)));
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

// Suma horas a la hora de pared 'YYYY-MM-DDTHH:MM' (como Python con zona: hora de reloj)
export function sumarHoras(fechaHora, horas) {
  const p = partesFecha(fechaHora);
  if (!p) return '';
  const d = new Date(Date.UTC(p.anio, p.mes - 1, p.dia, (p.hora || 0) + Number(horas || 0), p.minuto || 0));
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}T${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}

// 'dd/mm/aa'
export function fechaCorta(iso) {
  const p = partesFecha(iso);
  if (!p) return '';
  return `${pad2(p.dia)}/${pad2(p.mes)}/${String(p.anio).slice(-2)}`;
}

// 'dd/mm'
export function diaMes(iso) {
  const p = partesFecha(iso);
  if (!p) return '';
  return `${pad2(p.dia)}/${pad2(p.mes)}`;
}

// 'HH:MM' (vacío si la fecha no lleva hora)
export function horaDe(iso) {
  const p = partesFecha(iso);
  if (!p || p.hora === null) return '';
  return `${pad2(p.hora)}:${pad2(p.minuto || 0)}`;
}

// 'dd/mm/aa HH:MM' (o solo la fecha si no lleva hora)
export function fechaHoraCorta(iso) {
  const f = fechaCorta(iso);
  const h = horaDe(iso);
  return h ? `${f} ${h}` : f;
}

// Lote que tendrá la etiqueta (el número del día lo pone el servidor)
export function lotePrevisto(ahora = ahoraMadrid()) {
  const p = partesFecha(ahora);
  if (!p) return 'D';
  return `D${String(p.anio).slice(-2)}${pad2(p.mes)}${pad2(p.dia)}-...`;
}

// ==================== PRODUCTOS ====================

export const claveProducto = (p) => `${p?.tipo}:${p?.id}`;

export function textoVida(producto) {
  if (!producto) return '';
  if (esHastaEnvase(producto)) return TEXTO_HASTA_ENVASE;
  if (producto.tipo === 'plato') {
    const h = Number(producto.vida_horas);
    return Number.isFinite(h) && h > 0 ? `${h} h` : '';
  }
  const d = Number(producto.vida_dias);
  if (!Number.isFinite(d) || d <= 0) return '';
  return d === 1 ? '1 día' : `${d} días`;
}

export function textoAbiertas(n) {
  const num = Number(n) || 0;
  if (!num) return '';
  return num === 1 ? '1 abierta' : `${num} abiertas`;
}

// Texto de alérgenos de la etiqueta completa (mismo criterio que el backend)
export function textoAlergenos(producto) {
  if (!producto) return '';
  if (producto.tipo === 'plato' && producto.sin_datos_alergenos) return 'Alérgenos: consultar con cocina.';
  const contiene = (producto.alergenos || []).map((a) => String(a).toLowerCase());
  const trazas = (producto.trazas || []).map((a) => String(a).toLowerCase());
  if (!contiene.length && !trazas.length) return 'Sin alérgenos de declaración obligatoria.';
  const partes = [];
  if (contiene.length) partes.push(`Contiene: ${contiene.join(', ')}.`);
  if (trazas.length) partes.push(`Puede contener: ${trazas.join(', ')}.`);
  return partes.join(' ');
}

// ==================== FUENTE DEL PLAZO ====================

// De dónde sale el plazo: 'fabricante' | 'appcc' | 'cocina' | 'propuesta'
// (sin configuración guardada)
export const FUENTES_FILTRO = ['fabricante', 'appcc', 'cocina', 'propuesta'];

export const NOMBRE_FUENTE = {
  fabricante: 'Fabricante',
  appcc: 'APPCC Duetto',
  cocina: 'Cocina',
  propuesta: 'Propuesta',
};

// Abreviatura para el listado de Ingredientes
export const ABREVIATURA_FUENTE = {
  fabricante: 'Fab.',
  appcc: 'APPCC',
  cocina: 'Cocina',
  propuesta: 'Propuesta',
};

// Píldora de cada fuente (clases Tailwind explícitas)
export const CLASE_FUENTE = {
  fabricante: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  appcc: 'bg-blue-50 text-blue-800 border-blue-300',
  cocina: 'bg-gray-100 text-gray-700 border-gray-300',
  propuesta: 'bg-amber-50 text-amber-800 border-amber-300',
};

export function claveFuente(producto) {
  const f = producto?.fuente;
  return f === 'fabricante' || f === 'appcc' || f === 'cocina' ? f : 'propuesta';
}

// { clave, nombre, detalle, url }: el detalle es el texto que va debajo del chip
export function infoFuente(producto) {
  const clave = claveFuente(producto);
  let detalle = '';
  let url = '';
  if (clave === 'fabricante') {
    detalle = producto?.fuente_texto || '';
    url = /^https?:\/\//i.test(String(producto?.fuente_url || '')) ? producto.fuente_url : '';
  } else if (clave === 'appcc') {
    detalle = producto?.fuente_texto || '';
  } else if (clave === 'cocina') {
    const nombre = producto?.confirmado_por?.nombre;
    detalle = nombre ? `Fijado por ${nombre}` : '';
  } else if (producto?.fuente_sugerida === 'appcc' && producto?.fuente_texto_sugerido) {
    detalle = `Propuesta según APPCC: ${producto.fuente_texto_sugerido}`;
  }
  return { clave, nombre: NOMBRE_FUENTE[clave], detalle, url };
}

// Caducidad prevista de una etiqueta nueva, igual que POST /etiquetas/aperturas:
// - abrir: hoy + vida_dias (sin hora); si el envase caduca antes, manda el envase.
//   En modo "hasta la fecha del envase" la caducidad ES la del envase; sin ella
//   no se puede imprimir (faltaEnvase).
// - elaborar: ahora + vida_horas (con hora).
// - congelar: hoy + vida_congelado_dias (sin hora).
// Devuelve { caduca, conHora, porEnvase, faltaEnvase, texto }.
export function caducidadPrevista(producto, accion, { caducidadEnvase = '', ahora = ahoraMadrid() } = {}) {
  const hoy = String(ahora).slice(0, 10);
  if (!producto) return { caduca: '', conHora: false, porEnvase: false };
  if (accion === 'abrir' && esHastaEnvase(producto)) {
    if (caducidadEnvase && partesFecha(caducidadEnvase)) {
      return { caduca: caducidadEnvase.slice(0, 10), conHora: false, porEnvase: true };
    }
    return { caduca: '', conHora: false, porEnvase: false, faltaEnvase: true, texto: TEXTO_FALTA_ENVASE };
  }
  if (accion === 'elaborar') {
    const horas = Number(producto.vida_horas) || 24;
    return { caduca: sumarHoras(ahora, horas), conHora: true, porEnvase: false };
  }
  if (accion === 'congelar') {
    const dias = Number(producto.vida_congelado_dias) || VIDA_CONGELADO_DIAS;
    return { caduca: sumarDias(hoy, dias), conHora: false, porEnvase: false };
  }
  const dias = Number(producto.vida_dias) || 1;
  const caduca = sumarDias(hoy, dias);
  if (caducidadEnvase && partesFecha(caducidadEnvase) && caducidadEnvase < caduca) {
    return { caduca: caducidadEnvase.slice(0, 10), conHora: false, porEnvase: true };
  }
  return { caduca, conHora: false, porEnvase: false };
}

// Fecha en que empezó una etiqueta abierta según su acción
export function inicioApertura(apertura) {
  if (!apertura) return '';
  const porAccion = {
    abrir: apertura.abierto_en,
    elaborar: apertura.elaborado_en,
    congelar: apertura.congelado_en,
    descongelar: apertura.descongelado_en,
  };
  return porAccion[apertura.accion] || apertura.abierto_en || apertura.elaborado_en
    || apertura.congelado_en || apertura.descongelado_en || apertura.creado_en || '';
}

export const VERBO_ACCION = {
  abrir: 'Abierto',
  elaborar: 'Elaborado',
  congelar: 'Congelado',
  descongelar: 'Descongelado',
};

// ==================== DATOS DE LA VISTA PREVIA ====================

// Datos de fecha de la etiqueta (máx. 2), como el backend
function datosFecha({ accion, tipo, abierto_en: abierto, elaborado_en: elaborado, congelado_en: congelado, descongelado_en: descongelado }) {
  if (accion === 'elaborar') return [{ etiqueta: 'Elaborado', valor: fechaHoraCorta(elaborado) }];
  if (accion === 'congelar') {
    if (tipo === 'plato') {
      return [
        { etiqueta: 'Elaborado', valor: fechaHoraCorta(elaborado) },
        { etiqueta: 'Congelado', valor: fechaHoraCorta(congelado) },
      ];
    }
    return [{ etiqueta: 'Congelado', valor: fechaHoraCorta(congelado) }];
  }
  if (accion === 'descongelar') {
    return [
      { etiqueta: 'Congelado', valor: fechaCorta(congelado) },
      { etiqueta: 'Descongelado', valor: fechaHoraCorta(descongelado) },
    ];
  }
  return [{ etiqueta: 'Abierto', valor: fechaHoraCorta(abierto) }];
}

// Vista previa de una etiqueta todavía sin imprimir
export function vistaPrevia(producto, accion, { caducidadEnvase = '', loteFabricante = '', trasvasado = false, ahora = ahoraMadrid() } = {}) {
  if (!producto) return null;
  const congelar = accion === 'congelar';
  const conservacion = congelar ? CONGELADO : (producto.conservacion || REFRIGERADO);
  const rango = congelar
    ? RANGO_POR_CONSERVACION[CONGELADO]
    : (producto.rango || RANGO_POR_CONSERVACION[conservacion]);
  const formato = accion === 'abrir' && !trasvasado ? 'corta' : 'completa';
  const { caduca, conHora, faltaEnvase } = caducidadPrevista(producto, accion, { caducidadEnvase, ahora });
  return {
    formato,
    conservacion,
    rango,
    nombre: producto.nombre,
    datos_fecha: datosFecha({
      accion,
      tipo: producto.tipo,
      abierto_en: ahora,
      elaborado_en: ahora,
      congelado_en: ahora,
    }),
    // Sin la fecha del envase (modo "hasta la fecha del envase") el hueco queda marcado
    caduca_texto: faltaEnvase ? '--/--/--' : fechaCorta(caduca),
    caduca_hora: conHora ? horaDe(caduca) : '',
    alergenos_texto: formato === 'completa' ? textoAlergenos(producto) : '',
    avisos: [],
    lote: lotePrevisto(ahora),
    lote_fabricante: loteFabricante || '',
    caducidad_envase_texto: caducidadEnvase ? fechaCorta(caducidadEnvase) : '',
  };
}

// Vista de una etiqueta ya creada (apertura del servidor)
export function vistaDeApertura(apertura) {
  if (!apertura) return null;
  const conservacion = apertura.conservacion || REFRIGERADO;
  return {
    formato: apertura.formato || 'completa',
    conservacion,
    rango: apertura.rango || RANGO_POR_CONSERVACION[conservacion],
    nombre: apertura.nombre,
    datos_fecha: datosFecha(apertura),
    caduca_texto: fechaCorta(apertura.caduca),
    caduca_hora: apertura.caduca_con_hora ? horaDe(apertura.caduca) : '',
    alergenos_texto: apertura.formato === 'completa' ? (apertura.alergenos_texto || '') : '',
    avisos: apertura.avisos || [],
    lote: apertura.lote || '',
    lote_fabricante: apertura.lote_fabricante || '',
    caducidad_envase_texto: apertura.caducidad_envase ? fechaCorta(apertura.caducidad_envase) : '',
  };
}

// Tamaño del nombre según su longitud (mismo escalado que la plantilla)
export function tamanoNombrePt(nombre) {
  const n = String(nombre || '').length;
  if (n <= 24) return 10;
  if (n <= 32) return 9;
  if (n <= 42) return 8;
  if (n <= 60) return 7;
  return 6.5;
}
